import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    Slider,
    Tooltip,
    TextField,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { Category } from '../../types/Category';
import { suggestTreeFromCategory, fetchCategoryWithChildren } from '../../services/CategoryService';
import { historyService } from '../../services/HistoryService';
import api from '../../services/api';
import { useHelpContent } from '../../utils/HelpUtils';

interface GenerateSubtreeDialogProps {
    open: boolean;
    onClose: () => void;
    category: Category | null;
    onSuccess: (updatedCategoryId?: string, operationData?: any) => void;
}

const GenerateSubtreeDialog: React.FC<GenerateSubtreeDialogProps> = (props) => {
    const {
        open,
        onClose,
        category,
        onSuccess,
    } = props;
    
    const [loading, setLoading] = useState(false);
    const [suggestedTree, setSuggestedTree] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [showContent, setShowContent] = useState(true);
    const [maxDepth, setMaxDepth] = useState(3);
    const [useSuggestedName, setUseSuggestedName] = useState(false);
    const [mergingOccurred, setMergingOccurred] = useState(false);
    const [context, setContext] = useState('');
    const { getTooltipTextForState } = useHelpContent();

    // Helper function to merge duplicate categories at the same level
    const mergeDuplicateCategories = (node: any): { node: any, merged: boolean } => {
        if (!node || !node.children || node.children.length === 0) {
            return { node, merged: false };
        }

        // Group children by name (for categories) or type
        const categoryGroups = new Map<string, any[]>();
        const contentNodes: any[] = [];
        let hasMerging = false;

        for (const child of node.children) {
            if (child.type === 'category') {
                const name = child.name || 'Unnamed Category';
                if (!categoryGroups.has(name)) {
                    categoryGroups.set(name, []);
                }
                categoryGroups.get(name)!.push(child);
            } else if (child.type === 'content') {
                contentNodes.push(child);
            }
        }

        // Merge categories with the same name
        const mergedCategories: any[] = [];
        for (const [name, categories] of Array.from(categoryGroups.entries())) {
            if (categories.length === 1) {
                // Single category, just merge its children
                const childResult = mergeDuplicateCategories(categories[0]);
                const mergedCategory = {
                    ...categories[0],
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            } else {
                // Multiple categories with the same name, merge them
                console.log(`Merging ${categories.length} categories with name: "${name}"`);
                hasMerging = true;
                
                // Collect all children from all categories with this name
                const allChildren: any[] = [];
                for (const category of categories) {
                    if (category.children && category.children.length > 0) {
                        allChildren.push(...category.children);
                    }
                }

                // Create a single merged category
                const childResult = mergeDuplicateCategories({ children: allChildren });
                const mergedCategory = {
                    name: name,
                    type: 'category',
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            }
        }

        // Return the node with merged children
        return {
            node: {
                ...node,
                children: [...mergedCategories, ...contentNodes]
            },
            merged: hasMerging
        };
    };

    // Helper: recursively create categories and content
    const createTree = async (selectedCategoryId: string, node: any, withContent: boolean, originalContents?: any[], depth: number = 0) => {
        console.log('createTree called:', { selectedCategoryId, nodeName: node.name, depth });
        
        if (!node) return;
        
        if (node.children && node.children.length > 0) {
            console.log('Processing children:', node.children.length);
            
            const hasCategory = node.children.some((child: any) => child.type === 'category');
            const hasContent = node.children.some((child: any) => child.type === 'content');
            
            if (hasCategory && hasContent) {
                // Mixed children - this violates the constraint
                // Create categories first, then create a special category for unplaced content
                console.log('⚠️ Mixed children detected - creating special category for unplaced content');
                
                // First, create all category children
                for (const child of node.children) {
                    if (child.type === 'category') {
                        // Create category under the target category
                        const categoryRes = await api.post('/categories', {
                            parentId: selectedCategoryId,
                            name: child.name,
                            description: ''
                        });
                        const newCategory = categoryRes.data;
                        await createTree(newCategory.id, child, withContent, originalContents, depth + 1);
                    }
                }
                
                // Then, create a special category for unplaced content
                if (withContent) {
                    const uncategorizedCategoryRes = await api.post('/categories', {
                        parentId: selectedCategoryId,
                        name: 'Uncategorized Content',
                        description: 'Content that could not be placed in specific categories due to constraint violations'
                    });
                    const uncategorizedCategory = uncategorizedCategoryRes.data;
                    
                    // Place all content in this special category
                    await createContentNodes(uncategorizedCategory.id, node.children.filter((child: any) => child.type === 'content'), originalContents);
                }
            } else if (hasCategory) {
                // Only categories - process normally
                for (const child of node.children) {
                    if (child.type === 'category') {
                        const categoryRes = await api.post('/categories', {
                            parentId: selectedCategoryId,
                            name: child.name,
                            description: ''
                        });
                        const newCategory = categoryRes.data;
                        await createTree(newCategory.id, child, withContent, originalContents, depth + 1);
                    }
                }
            } else if (hasContent && withContent) {
                // Only content - process normally
                await createContentNodes(selectedCategoryId, node.children, originalContents);
            }
        }
    };

    // Helper function to create content nodes
    const createContentNodes = async (parentId: string, children: any[], originalContents?: any[]) => {
        // Track which original contents have been used to prevent duplicates
        const usedOriginalContents = new Set<string>();
        let contentNodesCreated = 0;
        const maxContentNodes = originalContents ? originalContents.length : 10; // Limit to prevent excessive content creation
        
        for (const child of children) {
            if (child.type === 'content') {
                // Skip if we've already created too many content nodes
                if (contentNodesCreated >= maxContentNodes) {
                    continue;
                }
                
                // Handle new index-based content format
                if (child.index !== undefined && originalContents) {
                    // Use the index to get the original content
                    const originalContent = originalContents[child.index];
                    if (originalContent && !usedOriginalContents.has(originalContent.id)) {
                        await api.post('/content', {
                            parentId: parentId || '',
                            text: originalContent.text,
                            subcase: originalContent.subcase,
                            id: ''
                        });
                        usedOriginalContents.add(originalContent.id);
                        contentNodesCreated++;
                    }
                } else if (child.text) {
                    // Fallback to old text-based format for backward compatibility
                    // Skip empty or very short content
                    if (!child.text || child.text.trim().length < 3) {
                        continue;
                    }
                    
                    // Try to find matching original content to preserve subcase
                    let subcase = 'Generated';
                    let matchedContentId = null;
                    
                    if (originalContents) {
                        // Clean up the AI-generated text by removing SUBCASE prefix if present
                        const cleanText = child.text.replace(/^SUBCASE:\s*/i, '').trim();
                        
                        // Find matching original content
                        for (const originalContent of originalContents) {
                            if (originalContent.text.includes(cleanText) || cleanText.includes(originalContent.text)) {
                                subcase = originalContent.subcase || 'Generated';
                                matchedContentId = originalContent.id;
                                break;
                            }
                        }
                    }
                    
                    // Only create if we haven't used this content before
                    if (!matchedContentId || !usedOriginalContents.has(matchedContentId)) {
                    await api.post('/content', {
                        parentId: parentId || '',
                            text: child.text,
                        subcase: subcase,
                        id: ''
                    });
                        if (matchedContentId) {
                            usedOriginalContents.add(matchedContentId);
                        }
                    contentNodesCreated++;
                    }
                }
            }
        }
    };

    const handleGenerate = async () => {
        if (!category) return;
        
        try {
            setLoading(true);
            setError(null);
            const result = await suggestTreeFromCategory(category.id, maxDepth, context);
            
            // Merge duplicate categories before displaying
            const mergeResult = mergeDuplicateCategories(result.tree);
            setMergingOccurred(mergeResult.merged);
            setSuggestedTree({
                ...result,
                tree: mergeResult.node
            });
        } catch (err: any) {
            setError(err.message || 'Failed to generate subtree');
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = async () => {
        if (!category) return;
        
        try {
            setLoading(true);
            setError(null);
            setMergingOccurred(false);
            const result = await suggestTreeFromCategory(category.id, maxDepth, context);
            
            // Merge duplicate categories before displaying
            const mergeResult = mergeDuplicateCategories(result.tree);
            setMergingOccurred(mergeResult.merged);
            setSuggestedTree({
                ...result,
                tree: mergeResult.node
            });
        } catch (err: any) {
            setError(err.message || 'Failed to regenerate subtree');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptWithContent = async () => {
        if (!category || !suggestedTree) return;
        
        console.log('handleAcceptWithContent called with useSuggestedName:', useSuggestedName);
        
        setLoading(true);
        setError(null);
        
        try {
            // Phase 1: Delete original content since it will be recreated in the new structure
            const originalContents = category.contents || [];
            if (originalContents.length > 0) {
                console.log('Phase 1: Deleting original content...');
                console.log('Deleting', originalContents.length, 'content items...');
                for (const content of originalContents) {
                    try {
                        await api.delete('/content', {
                            params: { contentId: content.id }
                        });
                    } catch (err) {
                        console.error('Failed to delete content:', content.id, err);
                    }
                }
            }
            
            // Phase 2: Handle renaming if needed
            let targetCategoryId = category.id;
            let renameSuccessful = false;
            
            if (useSuggestedName && suggestedTree.tree.name && suggestedTree.tree.name !== 'root') {
                console.log('Phase 2: Checking rename conditions...');
                
                // Check if we can rename this category (not a root category)
                if (category.parentId) {
                    console.log('✓ Category can be renamed - attempting rename');
                    console.log('Category details:', { 
                        categoryId: category.id, 
                        categoryName: category.name, 
                        categoryParentId: category.parentId,
                        newName: suggestedTree.tree.name
                    });
                    
                    try {
                        console.log('Making API call to rename category...');
                        const response = await api.put('/categories', {
                            parentId: category.id,
                            name: suggestedTree.tree.name,
                            description: category.description || ''
                        });
                        
                        console.log('✓ Successfully renamed category to:', suggestedTree.tree.name, 'Response:', response.data);
                        
                        // Use the updated category ID from the response
                        targetCategoryId = response.data.id;
                        renameSuccessful = true;
                        console.log('✓ Rename successful, new category ID:', targetCategoryId);
                    } catch (err: any) {
                        console.error('✗ Failed to rename category with suggested name:', err);
                        // Continue with original category ID since rename failed
                        targetCategoryId = category.id;
                        renameSuccessful = false;
                        console.log('Continuing with original category ID:', targetCategoryId);
                    }
                } else {
                    console.log('✗ Cannot rename root category, using original ID');
                    setUseSuggestedName(false);
                }
            } else {
                console.log('✗ Skipping rename - conditions not met');
            }
            
            // Phase 3: Create the tree structure under the target category
            console.log('Phase 3: Creating tree under category ID:', targetCategoryId);
            await createTree(targetCategoryId, suggestedTree.tree, true, suggestedTree.originalContents, 0);

            // Fetch the real Category tree after creation
            const realGeneratedTree = await fetchCategoryWithChildren(targetCategoryId);

            // Record the operation for undo/redo
            const operationData = {
                originalCategory: category,
                originalContents: originalContents,
                tempCategory: undefined, // No temp category needed since content is recreated in proper places
                generatedTree: realGeneratedTree, // Store the REAL Category object, not the AI template
                renamedCategoryId: renameSuccessful ? targetCategoryId : undefined,
                originalCategoryName: renameSuccessful ? category.name : undefined
            };

            console.log('Recording generateSubtree operation for undo/redo:', operationData);
            historyService.recordOperation({
                type: 'generateSubtree',
                data: operationData
            });
            
            // Pass the updated category ID to the success callback
            // If rename was successful, use the new ID; otherwise use the original ID
            const finalCategoryId = renameSuccessful ? targetCategoryId : category.id;
            onSuccess(finalCategoryId);
            setSuggestedTree(null); // Clear the suggested tree
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to create subtree with content');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptStructureOnly = async () => {
        if (!suggestedTree || !category) return;
        
        setLoading(true);
        setError(null);
        
        try {
            let targetCategoryId = category.id;
            let renameSuccessful = false;
            let newCategoryId = category.id; // Store the new category ID separately

            // Phase 1: Create temp category and move original content BEFORE rename
            console.log('Phase 1: Creating temp category and moving original content...');
            const tempCategoryRes = await api.post('/categories', {
                parentId: category.id, // Create under the selected category, not the parent
                name: `Original Content - ${category.name}`,
                description: 'Temporary category containing original content'
            });
            const tempCategory = tempCategoryRes.data;

            // Move all original content to temp category BEFORE rename
            const contentsToMove = category.contents || [];
            console.log('Moving', contentsToMove.length, 'content items to temp category...');
            for (const content of contentsToMove) {
                try {
                   const response = await api.put('/content/move', {
                       contentId: content.id,
                       newParentId: tempCategory?.id,
                     },
                     {
                       headers: {
                         'Content-Type': 'application/x-www-form-urlencoded'
                     }
                    });
                } catch (err) {
                    console.error('Failed to move content:', content.id, err);
                }
            }

            // Phase 2: Rename category if needed
            if (useSuggestedName && suggestedTree.tree.name && suggestedTree.tree.name !== 'root') {
                console.log('Phase 2: Checking rename conditions...');
                
                // Check if we can rename this category (not a root category)
                if (category.parentId) {
                    console.log('✓ Category can be renamed - attempting rename');
                    console.log('Category details:', { 
                        categoryId: category.id, 
                        categoryName: category.name, 
                        categoryParentId: category.parentId,
                        newName: suggestedTree.tree.name
                    });
                    
                    try {
                        console.log('Making API call to rename category...');
                        const response = await api.put('/categories', {
                            parentId: category.id,
                            name: suggestedTree.tree.name,
                            description: category.description || ''
                        });
                        
                        console.log('✓ Successfully renamed category to:', suggestedTree.tree.name, 'Response:', response.data);
                        
                        // Use the updated category ID from the response
                        newCategoryId = response.data.id; // Store in separate variable
                        targetCategoryId = newCategoryId; // Use for tree creation
                        renameSuccessful = true;
                        console.log('✓ Rename successful, new category ID:', newCategoryId);
                    } catch (err: any) {
                        console.error('✗ Failed to rename category with suggested name:', err);
                        // Continue with original category ID since rename failed
                        targetCategoryId = category.id;
                        newCategoryId = category.id;
                        renameSuccessful = false;
                        console.log('Continuing with original category ID:', targetCategoryId);
                    }
                } else {
                    console.log('✗ Cannot rename root category, using original ID');
                    setUseSuggestedName(false);
                }
            } else {
                console.log('✗ Skipping rename - conditions not met');
            }
            
            // Phase 3: Create the tree structure under the target category
            console.log('Phase 3: Creating tree under category ID:', targetCategoryId);
            await createTree(targetCategoryId, suggestedTree.tree, false, suggestedTree.originalContents, 0);
            
            // Fetch the real Category tree after creation
            const realGeneratedTree = await fetchCategoryWithChildren(targetCategoryId);

            // Record the operation for undo/redo
            const operationData = {
                originalCategory: category,
                originalContents: category.contents || [],
                tempCategory: tempCategory,
                generatedTree: realGeneratedTree, // Store the REAL Category object, not the AI template
                renamedCategoryId: renameSuccessful ? newCategoryId : undefined,
                originalCategoryName: renameSuccessful ? category.name : undefined
            };

            console.log('Recording generateSubtree operation for undo/redo:', operationData);
            historyService.recordOperation({
                type: 'generateSubtree',
                data: operationData
            });
            
            // Pass the updated category ID to the success callback
            // If rename was successful, use the new ID; otherwise use the original ID
            const finalCategoryId = renameSuccessful ? newCategoryId : category.id;
            onSuccess(finalCategoryId);
            setSuggestedTree(null); // Clear the suggested tree
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to create subtree structure');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSuggestedTree(null);
        setError(null);
        setShowContent(true);
        setMaxDepth(3);
        setUseSuggestedName(false);
        setMergingOccurred(false);
        setContext('');
        onClose();
    };

    // Helper to detect RTL (Hebrew/Arabic)
    const isRTL = suggestedTree && suggestedTree.tree && /[\u0590-\u05FF\u0600-\u06FF]/.test(suggestedTree.tree.name);

    // Helper function to detect content categories
    const isContentCategory = (node: any): boolean => {
        if (!node.children || node.children.length === 0) {
            return true; // No children = content category
        }
        // Has children of type 'content' = content category
        return node.children.some((child: any) => child.type === 'content');
    };

    // Helper to render tree preview
    const renderTree = (node: any, path: string = '', level: number = 0): React.ReactNode[] => {
        if (!node) return [];
        const currentPath = path + (node.name || node.text || '');
        const padding = isRTL ? { pr: level * 2 } : { pl: level * 2 };

        // Category node (including root)
        if (node.type === 'category' && node.name) {
            const children = Array.isArray(node.children)
                ? node.children.flatMap((child: any, idx: number) => renderTree(child, currentPath + '-' + idx, level + 1))
                : [];
            const isContentCat = isContentCategory(node);
            return [
                <Box key={currentPath} sx={{ display: 'flex', alignItems: 'center', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    {isContentCat ? (
                        <DescriptionIcon sx={{ color: '#87CEEB', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    ) : (
                    <FolderIcon sx={{ color: '#1976d2', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 700, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                        {node.name}
                    </Typography>
                </Box>,
                ...children
            ];
        }

        // Content node
        if (showContent && node.type === 'content') {
            let displayText = '';
            let subcaseText = '';
            
            // Handle new index-based content format
            if (node.index !== undefined && suggestedTree?.originalContents) {
                const originalContent = suggestedTree.originalContents[node.index];
                if (originalContent) {
                    displayText = originalContent.text;
                    subcaseText = originalContent.subcase || '';
                }
            } else if (node.text) {
                // Fallback to old text-based format for backward compatibility
                displayText = node.text;
            }
            
            if (displayText) {
                return [
                    <Box key={currentPath} sx={{ display: 'flex', flexDirection: 'column', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                        {subcaseText && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <StickyNote2OutlinedIcon sx={{ color: '#ff9800', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, fontSize: 18, mt: 0.5 }} />
                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 600, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left' }} dir="auto">
                                    {subcaseText}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                                {displayText}
                            </Typography>
                        </Box>
                    </Box>
                ];
            }
        }

        return [];
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Generate Subtree from Content
                {category && (
                    <Typography variant="subtitle2" color="textSecondary">
                        Category: {category.name}
                    </Typography>
                )}
            </DialogTitle>
            
            <DialogContent sx={{ position: 'relative' }}>
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255,255,255,0.7)',
                            zIndex: 10,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {!suggestedTree && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            This will analyze the content of the selected category and suggest a hierarchical structure.
                        </Typography>
                        
                        <Tooltip title={getTooltipTextForState('suggest-tree-context', true)} placement="top">
                            <TextField
                                fullWidth
                                label="Context (Optional)"
                                value={context}
                                onChange={(e) => setContext(e.target.value)}
                                placeholder="Provide context to help AI generate better category names and clustering..."
                                multiline
                                rows={3}
                                margin="normal"
                                helperText="Describe the purpose, domain, or context of this content to improve category naming and organization"
                            />
                        </Tooltip>
                        
                        <FormControlLabel
                            control={
                                <Tooltip title={getTooltipTextForState('generate-subtree', true)} placement="top">
                                    <Slider
                                        value={maxDepth}
                                        onChange={(_, value) => setMaxDepth(value as number)}
                                        min={1}
                                        max={5}
                                        marks
                                        valueLabelDisplay="auto"
                                    />
                                </Tooltip>
                            }
                            label={`Maximum Depth: ${maxDepth}`}
                            labelPlacement="top"
                        />
                        
                        <Tooltip title={getTooltipTextForState('generate-subtree', true)} placement="top">
                            <Button
                                variant="contained"
                                onClick={handleGenerate}
                                disabled={loading || !category}
                                sx={{ mt: 2 }}
                            >
                                Generate Subtree
                            </Button>
                        </Tooltip>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {mergingOccurred && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Some categories with duplicate names were automatically merged to maintain a valid structure.
                    </Alert>
                )}

                {suggestedTree && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Suggested Structure
                        </Typography>
                        
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 200 }}>
                                <Typography variant="body2" gutterBottom>
                                    Maximum Depth: {maxDepth}
                                </Typography>
                                <Tooltip title={getTooltipTextForState('subtree-max-depth', true)} placement="top">
                                    <Slider
                                        value={maxDepth}
                                        onChange={(_, value) => setMaxDepth(value as number)}
                                        min={1}
                                        max={5}
                                        marks
                                        valueLabelDisplay="auto"
                                        disabled={loading}
                                    />
                                </Tooltip>
                            </Box>
                            
                            <Tooltip title={getTooltipTextForState('subtree-retry', true)} placement="top">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleRetry}
                                    disabled={loading}
                                    sx={{ minWidth: 100 }}
                                >
                                    Retry
                                </Button>
                            </Tooltip>
                        </Box>
                        
                        <FormControlLabel
                            control={
                                <Tooltip title={getTooltipTextForState('generate-subtree', true)} placement="top">
                                    <Checkbox
                                        checked={showContent}
                                        onChange={(e) => setShowContent(e.target.checked)}
                                    />
                                </Tooltip>
                            }
                            label="Show content preview"
                        />
                        
                        <FormControlLabel
                            control={
                                <Tooltip title={getTooltipTextForState('generate-subtree', true)} placement="top">
                                    <Checkbox
                                        checked={useSuggestedName}
                                        onChange={(e) => setUseSuggestedName(e.target.checked)}
                                        disabled={!category?.parentId} // Disable if root category
                                    />
                                </Tooltip>
                            }
                            label={
                                !category?.parentId 
                                    ? `Cannot rename root category: "${suggestedTree.tree.name}"`
                                    : `Use AI-suggested name: "${suggestedTree.tree.name}"`
                            }
                        />
                        
                        <Box sx={{ 
                            maxHeight: 400, 
                            overflow: 'auto', 
                            border: '1px solid #ddd', 
                            borderRadius: 1, 
                            p: 2,
                            mt: 2 
                        }}>
                            {renderTree(suggestedTree.tree)}
                        </Box>
                        
                        <Typography variant="body2" sx={{ mt: 2, mb: 2, color: 'text.secondary' }}>
                            <strong>Note:</strong> Choose your preferred option:
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                • Generate with Content: Creates the structure and includes the AI-generated content nodes (original content is deleted and recreated in the new structure)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                • Generate Structure Only: Creates only the category structure without content nodes (original content is preserved in a temporary category)
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                {suggestedTree && (
                    <>
                        <Tooltip title={getTooltipTextForState('generate-subtree', true)} placement="top">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAcceptWithContent}
                                disabled={loading}
                            >
                                Generate with Content
                            </Button>
                        </Tooltip>
                        <Tooltip title={getTooltipTextForState('generate-subtree', true)} placement="top">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAcceptStructureOnly}
                                disabled={loading}
                            >
                                Generate Structure Only
                            </Button>
                        </Tooltip>
                    </>
                )}
                <Tooltip title={getTooltipTextForState('content-cancel', true)} placement="top">
                    <Button onClick={handleClose}>
                        Cancel
                    </Button>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default GenerateSubtreeDialog; 